import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, User, Mail, Phone, MapPin, Package, DollarSign, Calendar,
  Plus, Edit2, Trash2, Star, CreditCard, Truck, FileText, ChevronDown,
  ChevronUp, Copy, ExternalLink, Clock, CheckCircle, XCircle, AlertCircle,
  Home, Building, Save, X, MessageSquare, LogIn, Key, Shield, Globe,
  Award, RefreshCw, Ban, Send
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import QuoteBuilderPage from '../quotes/QuoteBuilderPage';
import { useSiteFeatureFlags } from '../../hooks/useSiteFeatureFlags';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const AdminCustomerDashboard = ({ customerId: propCustomerId }) => {
  const { customerId: paramCustomerId } = useParams();
  const customerId = propCustomerId || paramCustomerId;
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const { quotes_enabled: quotesEnabled } = useSiteFeatureFlags();
  
  const [loading, setLoading] = useState(true);
  const [customerData, setCustomerData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Address Modal State
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({
    label: 'Home',
    first_name: '',
    last_name: '',
    address1: '',
    address2: '',
    city: '',
    state: 'AL',
    zip_code: '',
    phone: '',
    is_default: false
  });
  const [savingAddress, setSavingAddress] = useState(false);
  
  // Profile Edit State
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
    notes: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Order Detail State
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  
  // Review State
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewingOrder, setReviewingOrder] = useState(null);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    title: '',
    content: ''
  });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [customerReviews, setCustomerReviews] = useState([]);
  const [reviewedOrders, setReviewedOrders] = useState(new Set());
  const [impersonating, setImpersonating] = useState(false);
  const [linkedLeadId, setLinkedLeadId] = useState(null);

  // License state
  const [licenses, setLicenses] = useState([]);
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [extensionProducts, setExtensionProducts] = useState([]);
  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [grantLoading, setGrantLoading] = useState(false);
  const [editLicenseModal, setEditLicenseModal] = useState(null); // license being edited
  const [grantForm, setGrantForm] = useState({
    product_id: '',
    license_tier: 'single',
    price: '',
    expires_at: '',
    max_sites: '',
    notes: '',
    send_email: true,
    quantity: 1,
  });

  useEffect(() => {
    if (customerId) {
      fetchCustomerData();
      fetchCustomerReviews();
    }
  }, [customerId]);

  // Fetch licenses when "licenses" tab is active
  useEffect(() => {
    if (activeTab === 'licenses' && customerId) {
      fetchLicenses();
    }
  }, [activeTab, customerId]);

  const fetchLicenses = async () => {
    setLicenseLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/customers/${customerId}/licenses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLicenses(res.data || []);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load licenses', variant: 'destructive' });
    } finally {
      setLicenseLoading(false);
    }
  };

  const fetchExtensionProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/extension-products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExtensionProducts(res.data || []);
    } catch (_) {}
  };

  const handleGrantLicense = async (e) => {
    e.preventDefault();
    if (!grantForm.product_id) {
      toast({ title: 'Select a product', variant: 'destructive' });
      return;
    }
    setGrantLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        product_id: grantForm.product_id,
        license_tier: grantForm.license_tier,
        price: grantForm.price !== '' ? parseFloat(grantForm.price) : null,
        expires_at: grantForm.expires_at || null,
        max_sites: grantForm.max_sites !== '' ? parseInt(grantForm.max_sites, 10) : null,
        notes: grantForm.notes || null,
        send_email: grantForm.send_email,
        quantity: Math.max(1, parseInt(grantForm.quantity, 10) || 1),
      };
      const res = await axios.post(`${API}/admin/customers/${customerId}/grant-license`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({
        title: `${res.data.count} license${res.data.count > 1 ? 's' : ''} granted!`,
        description: `${grantForm.send_email ? 'Customer notified by email.' : ''}`,
      });
      setGrantModalOpen(false);
      setGrantForm({ product_id: '', license_tier: 'single', price: '', expires_at: '', max_sites: '', notes: '', send_email: true, quantity: 1 });
      fetchLicenses();
    } catch (err) {
      toast({ title: 'Grant failed', description: err.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setGrantLoading(false);
    }
  };

  const handleRevokeLicense = async (licenseId) => {
    if (!window.confirm('Revoke this license? The customer will lose access immediately.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/licenses/${licenseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'License revoked' });
      fetchLicenses();
    } catch (err) {
      toast({ title: 'Failed to revoke', description: err.response?.data?.detail, variant: 'destructive' });
    }
  };

  const handleReactivateLicense = async (licenseId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/admin/licenses/${licenseId}`, { status: 'active' }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'License reactivated' });
      fetchLicenses();
    } catch (err) {
      toast({ title: 'Failed to reactivate', description: err.response?.data?.detail, variant: 'destructive' });
    }
  };

  const handleUpdateLicense = async (licenseId, updates) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/admin/licenses/${licenseId}`, updates, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'License updated' });
      setEditLicenseModal(null);
      fetchLicenses();
    } catch (err) {
      toast({ title: 'Failed to update', description: err.response?.data?.detail, variant: 'destructive' });
    }
  };

  const handleToggleLicenseJoomla911 = async (licenseId, enabled) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/admin/licenses/${licenseId}/joomla911`, { enabled }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: enabled ? 'Joomla 911 access restored for this license' : 'Joomla 911 access revoked for this license' });
      fetchLicenses();
    } catch (err) {
      toast({ title: 'Failed to update Joomla 911 access', description: err.response?.data?.detail, variant: 'destructive' });
    }
  };

  const handleToggleSiteJoomla911 = async (licenseId, siteKey, enabled) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/admin/licenses/${licenseId}/sites/${siteKey}/joomla911`, { enabled }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: enabled ? 'Joomla 911 access restored for this site' : 'Joomla 911 access revoked for this site' });
      fetchLicenses();
    } catch (err) {
      toast({ title: 'Failed to update Joomla 911 access', description: err.response?.data?.detail, variant: 'destructive' });
    }
  };

  const copyKey = (key) => {
    navigator.clipboard?.writeText(key);
    toast({ title: 'Key copied!' });
  };

  const fetchCustomerData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/customers/${customerId}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerData(response.data);
      await fetchLinkedLead(response.data.customer?.id);
      setProfileForm({
        name: response.data.customer?.name || '',
        phone: response.data.customer?.phone || '',
        notes: response.data.customer?.notes || ''
      });
    } catch (error) {
      console.error('Error fetching customer data:', error);
      toast({ title: 'Error', description: 'Failed to load customer data', variant: 'destructive' });
    }
    setLoading(false);
  };

  const fetchLinkedLead = async (id) => {
    if (!id) {
      setLinkedLeadId(null);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/customers/${id}/quote-lead-link`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLinkedLeadId(response.data?.lead_id || null);
    } catch (_error) {
      setLinkedLeadId(null);
    }
  };

  const fetchCustomerReviews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reviews/my-reviews/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerReviews(response.data || []);
      // Build set of reviewed order IDs
      const reviewed = new Set(response.data.map(r => r.order_id));
      setReviewedOrders(reviewed);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const openReviewModal = (order) => {
    setReviewingOrder(order);
    setReviewForm({ rating: 5, title: '', content: '' });
    setReviewModalOpen(true);
  };

  const handleSubmitReview = async () => {
    if (!reviewForm.title || !reviewForm.content) {
      toast({ title: 'Error', description: 'Please fill in all fields', variant: 'destructive' });
      return;
    }
    
    setSubmittingReview(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/reviews/submit`, {
        order_id: reviewingOrder.id,
        rating: reviewForm.rating,
        title: reviewForm.title,
        content: reviewForm.content
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast({ title: 'Success', description: 'Review submitted! It will be visible after approval.' });
      setReviewModalOpen(false);
      fetchCustomerReviews();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit review';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    }
    setSubmittingReview(false);
  };

  const canReviewOrder = (order) => {
    // Check if order has been reviewed
    if (reviewedOrders.has(order.id)) return false;
    // Check if order status allows review
    return ['completed', 'delivered', 'shipped', 'paid'].includes(order.status);
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/users/customers/${customerId}/profile`, profileForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Profile updated' });
      setEditingProfile(false);
      fetchCustomerData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update profile', variant: 'destructive' });
    }
    setSavingProfile(false);
  };

  const openAddressModal = (address = null) => {
    if (address) {
      setEditingAddress(address);
      setAddressForm({
        label: address.label || 'Home',
        first_name: address.first_name || '',
        last_name: address.last_name || '',
        address1: address.address1 || '',
        address2: address.address2 || '',
        city: address.city || '',
        state: address.state || 'AL',
        zip_code: address.zip_code || '',
        phone: address.phone || '',
        is_default: address.is_default || false
      });
    } else {
      setEditingAddress(null);
      setAddressForm({
        label: 'Home',
        first_name: customerData?.customer?.name?.split(' ')[0] || '',
        last_name: customerData?.customer?.name?.split(' ').slice(1).join(' ') || '',
        address1: '',
        address2: '',
        city: '',
        state: 'AL',
        zip_code: '',
        phone: customerData?.customer?.phone || '',
        is_default: customerData?.addresses?.length === 0
      });
    }
    setAddressModalOpen(true);
  };

  const handleSaveAddress = async () => {
    if (!addressForm.first_name || !addressForm.last_name || !addressForm.address1 || 
        !addressForm.city || !addressForm.state || !addressForm.zip_code) {
      toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }
    
    setSavingAddress(true);
    try {
      const token = localStorage.getItem('token');
      if (editingAddress) {
        await axios.put(
          `${API}/users/customers/${customerId}/addresses/${editingAddress.id}`,
          addressForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({ title: 'Success', description: 'Address updated' });
      } else {
        await axios.post(
          `${API}/users/customers/${customerId}/addresses`,
          addressForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({ title: 'Success', description: 'Address added' });
      }
      setAddressModalOpen(false);
      fetchCustomerData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save address', variant: 'destructive' });
    }
    setSavingAddress(false);
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Delete this address?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/users/customers/${customerId}/addresses/${addressId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Address deleted' });
      fetchCustomerData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete address', variant: 'destructive' });
    }
  };

  const handleSetDefaultAddress = async (addressId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/users/customers/${customerId}/addresses/${addressId}`,
        { is_default: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Success', description: 'Default address updated' });
      fetchCustomerData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update default', variant: 'destructive' });
    }
  };

  const handleImpersonateCustomer = async () => {
    setImpersonating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/customers/${customerId}/impersonate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { access_token, user } = response.data;
      const result = await startImpersonation(access_token, user);
      if (!result.success) {
        throw new Error(result.error || 'Could not start impersonation');
      }

      toast({
        title: 'Impersonation active',
        description: `Now signed in as ${user?.email || customerData?.customer?.email}`,
      });
      window.location.assign('/account');
    } catch (error) {
      toast({
        title: 'Impersonation failed',
        description: error.response?.data?.detail || error.message || 'Unable to impersonate this customer.',
        variant: 'destructive',
      });
    } finally {
      setImpersonating(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      processing: { color: 'bg-blue-100 text-blue-800', icon: Package },
      awaiting_payment: { color: 'bg-orange-100 text-orange-800', icon: AlertCircle },
      shipped: { color: 'bg-purple-100 text-purple-800', icon: Truck },
      delivered: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle }
    };
    return configs[status] || { color: 'bg-gray-100 text-gray-800', icon: Package };
  };

  const renderStars = (rating, interactive = false, onChange = null) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${
              star <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'
            } ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
            onClick={() => interactive && onChange && onChange(star)}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!customerData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Customer not found</p>
        <Button onClick={() => navigate('/admin/user-management/customers')} className="mt-4">
          Back to Customers
        </Button>
      </div>
    );
  }

  const { customer, stats, addresses, orders } = customerData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/admin/user-management/customers')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Customer Dashboard</h1>
            <p className="text-gray-500">{customer.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleImpersonateCustomer}
            disabled={impersonating}
            data-testid="impersonate-customer-dashboard-button"
          >
            <LogIn className="w-4 h-4 mr-2" />
            {impersonating ? 'Impersonating...' : 'Impersonate User'}
          </Button>
          <Badge className={customer.customer_type === 'wholesale' ? 'bg-purple-600' : 'bg-gray-500'}>
            {customer.customer_type === 'wholesale' ? 'Wholesale' : 'Retail'}
          </Badge>
        </div>
      </div>

      {/* Customer Profile Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 bg-gradient-to-br from-purple-600 to-amber-500 rounded-full flex items-center justify-center text-white text-3xl font-bold">
              {customer.name?.charAt(0).toUpperCase()}
            </div>
            
            <div className="flex-1">
              {editingProfile ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={profileForm.name}
                        onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      value={profileForm.notes}
                      onChange={(e) => setProfileForm({...profileForm, notes: e.target.value})}
                      placeholder="Internal notes about this customer..."
                      rows={2}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSaveProfile} disabled={savingProfile}>
                      <Save className="w-4 h-4 mr-2" />
                      {savingProfile ? 'Saving...' : 'Save'}
                    </Button>
                    <Button variant="outline" onClick={() => setEditingProfile(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold">{customer.name}</h2>
                    <Button variant="ghost" size="sm" onClick={() => setEditingProfile(true)}>
                      <Edit2 className="w-4 h-4 mr-1" /> Edit
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-4 mt-2 text-gray-600">
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" /> {customer.email}
                    </span>
                    {customer.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" /> {customer.phone}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" /> 
                      Customer since {new Date(customer.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {customer.notes && (
                    <p className="mt-3 text-sm text-gray-500 bg-gray-50 p-2 rounded">
                      <strong>Notes:</strong> {customer.notes}
                    </p>
                  )}
                  {customer.custom_discount_percentage && (
                    <Badge className="mt-2 bg-green-100 text-green-800">
                      Custom Discount: {customer.custom_discount_percentage}% off
                    </Badge>
                  )}
                </>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-2xl font-bold text-purple-600">{stats.total_orders}</p>
                <p className="text-sm text-gray-500">Orders</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-2xl font-bold text-green-600">${stats.total_spent.toLocaleString()}</p>
                <p className="text-sm text-gray-500">Total Spent</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-4">
                <p className="text-2xl font-bold text-amber-600">${stats.avg_order_value.toFixed(2)}</p>
                <p className="text-sm text-gray-500">Avg Order</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid w-full ${quotesEnabled ? 'grid-cols-6' : 'grid-cols-5'} lg:w-auto lg:inline-flex`}>
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <User className="w-4 h-4" /> Overview
          </TabsTrigger>
          <TabsTrigger value="addresses" className="flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Addresses ({addresses.length})
          </TabsTrigger>
          <TabsTrigger value="orders" className="flex items-center gap-2">
            <Package className="w-4 h-4" /> Orders ({orders.length})
          </TabsTrigger>
          <TabsTrigger value="invoices" className="flex items-center gap-2">
            <FileText className="w-4 h-4" /> Invoices
          </TabsTrigger>
          <TabsTrigger value="licenses" className="flex items-center gap-2" data-testid="customer-licenses-tab">
            <Key className="w-4 h-4" /> Licenses {licenses.length > 0 && <span className="ml-1 bg-blue-100 text-blue-700 text-xs font-bold px-1.5 py-0.5 rounded-full">{licenses.length}</span>}
          </TabsTrigger>
          {quotesEnabled && (
            <TabsTrigger value="quotes-contracts-esign" className="flex items-center gap-2" data-testid="customer-quotes-contracts-tab-trigger">
              <FileText className="w-4 h-4" /> Quotes
            </TabsTrigger>
          )}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Recent Orders */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="w-5 h-5 text-purple-600" />
                  Recent Orders
                </CardTitle>
              </CardHeader>
              <CardContent>
                {orders.length > 0 ? (
                  <div className="space-y-3">
                    {orders.slice(0, 5).map((order) => {
                      const statusConfig = getStatusConfig(order.status);
                      const StatusIcon = statusConfig.icon;
                      return (
                        <div 
                          key={order.id} 
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                          onClick={() => { setSelectedOrder(order); setOrderDetailOpen(true); }}
                        >
                          <div>
                            <p className="font-bold text-gray-900">{order.order_number}</p>
                            <p className="text-sm text-gray-500">
                              {new Date(order.created_at).toLocaleDateString()} • {order.items_count} items
                            </p>
                          </div>
                          <div className="text-right">
                            <Badge className={statusConfig.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {order.status}
                            </Badge>
                            <p className="font-semibold mt-1">${order.total?.toFixed(2)}</p>
                          </div>
                        </div>
                      );
                    })}
                    {orders.length > 5 && (
                      <Button variant="outline" className="w-full" onClick={() => setActiveTab('orders')}>
                        View All Orders
                      </Button>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-6">No orders yet</p>
                )}
              </CardContent>
            </Card>

            {/* Shipping Addresses */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-purple-600" />
                  Shipping Addresses
                </CardTitle>
                <Button size="sm" onClick={() => openAddressModal()}>
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </CardHeader>
              <CardContent>
                {addresses.length > 0 ? (
                  <div className="space-y-3">
                    {addresses.slice(0, 3).map((addr) => (
                      <div key={addr.id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              {addr.label === 'Home' ? <Home className="w-4 h-4" /> : <Building className="w-4 h-4" />}
                              <span className="font-medium">{addr.label}</span>
                              {addr.is_default && <Badge className="bg-purple-100 text-purple-700 text-xs">Default</Badge>}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              {addr.first_name} {addr.last_name}<br />
                              {addr.address1}{addr.address2 && `, ${addr.address2}`}<br />
                              {addr.city}, {addr.state} {addr.zip_code}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openAddressModal(addr)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {addresses.length > 3 && (
                      <Button variant="outline" className="w-full" onClick={() => setActiveTab('addresses')}>
                        View All Addresses
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <MapPin className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No addresses saved</p>
                    <Button size="sm" className="mt-2" onClick={() => openAddressModal()}>
                      <Plus className="w-4 h-4 mr-1" /> Add Address
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Addresses Tab */}
        <TabsContent value="addresses">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Shipping Addresses</CardTitle>
              <Button onClick={() => openAddressModal()}>
                <Plus className="w-4 h-4 mr-2" /> Add New Address
              </Button>
            </CardHeader>
            <CardContent>
              {addresses.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {addresses.map((addr) => (
                    <div key={addr.id} className={`p-4 rounded-lg border-2 ${addr.is_default ? 'border-purple-500 bg-purple-50' : 'border-gray-200 bg-white'}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {addr.label === 'Home' ? <Home className="w-5 h-5 text-purple-600" /> : <Building className="w-5 h-5 text-purple-600" />}
                          <span className="font-bold">{addr.label}</span>
                        </div>
                        {addr.is_default && <Badge className="bg-purple-600 text-white">Default</Badge>}
                      </div>
                      <div className="text-sm text-gray-700 space-y-1">
                        <p className="font-medium">{addr.first_name} {addr.last_name}</p>
                        <p>{addr.address1}</p>
                        {addr.address2 && <p>{addr.address2}</p>}
                        <p>{addr.city}, {addr.state} {addr.zip_code}</p>
                        {addr.phone && <p className="text-gray-500">{addr.phone}</p>}
                      </div>
                      <div className="flex gap-2 mt-4 pt-3 border-t">
                        <Button variant="outline" size="sm" onClick={() => openAddressModal(addr)}>
                          <Edit2 className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        {!addr.is_default && (
                          <Button variant="outline" size="sm" onClick={() => handleSetDefaultAddress(addr.id)}>
                            <Star className="w-4 h-4 mr-1" /> Set Default
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDeleteAddress(addr.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-700">No Addresses</h3>
                  <p className="text-gray-500 mb-4">Add shipping addresses for drop shipping and quick checkout</p>
                  <Button onClick={() => openAddressModal()}>
                    <Plus className="w-4 h-4 mr-2" /> Add First Address
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders">
          <Card>
            <CardHeader>
              <CardTitle>Order History</CardTitle>
            </CardHeader>
            <CardContent>
              {orders.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-3 px-4 font-medium text-gray-500">Order</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-500">Date</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-500">Items</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-500">Status</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-500">Payment</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-500">Total</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => {
                        const statusConfig = getStatusConfig(order.status);
                        return (
                          <tr key={order.id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4">
                              <span className="font-bold text-gray-900">{order.order_number}</span>
                            </td>
                            <td className="py-3 px-4 text-gray-600">
                              {new Date(order.created_at).toLocaleDateString()}
                            </td>
                            <td className="py-3 px-4">
                              {order.items_count} items
                            </td>
                            <td className="py-3 px-4">
                              <Badge className={statusConfig.color}>{order.status}</Badge>
                            </td>
                            <td className="py-3 px-4 text-gray-600 capitalize">
                              {order.payment_method}
                            </td>
                            <td className="py-3 px-4 text-right font-semibold">
                              ${order.total?.toFixed(2)}
                            </td>
                            <td className="py-3 px-4 text-right flex gap-2 justify-end">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => { setSelectedOrder(order); setOrderDetailOpen(true); }}
                              >
                                View
                              </Button>
                              {canReviewOrder(order) ? (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-amber-600 border-amber-600 hover:bg-amber-50"
                                  onClick={() => openReviewModal(order)}
                                >
                                  <Star className="w-3 h-3 mr-1" /> Review
                                </Button>
                              ) : reviewedOrders.has(order.id) && (
                                <Badge className="bg-green-100 text-green-700">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Reviewed
                                </Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No orders found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <Card>
            <CardHeader>
              <CardTitle>Invoices</CardTitle>
            </CardHeader>
            <CardContent>
              {orders.filter(o => o.status === 'completed' || o.status === 'delivered').length > 0 ? (
                <div className="space-y-3">
                  {orders.filter(o => o.status === 'completed' || o.status === 'delivered').map((order) => (
                    <div key={order.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <FileText className="w-10 h-10 text-purple-600" />
                        <div>
                          <p className="font-bold text-gray-900">Invoice #{order.order_number}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(order.created_at).toLocaleDateString()} • {order.items_count} items
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-xl font-bold">${order.total?.toFixed(2)}</span>
                        <Button variant="outline" size="sm">
                          <FileText className="w-4 h-4 mr-1" /> Download
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No invoices available yet</p>
                  <p className="text-sm text-gray-400">Invoices are generated for completed orders</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {quotesEnabled && (
          <TabsContent value="quotes-contracts-esign" data-testid="customer-quotes-contracts-tab-content">
            <Card>
              <CardHeader>
                <CardTitle>Quotes</CardTitle>
              </CardHeader>
              <CardContent>
                {linkedLeadId ? (
                  <QuoteBuilderPage leadId={linkedLeadId} quoteId="new" />
                ) : (
                  <div className="text-sm text-gray-500" data-testid="customer-quotes-no-linked-lead">No linked lead found</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ── Licenses Tab ──────────────────────────────────────────── */}
        <TabsContent value="licenses" className="space-y-4" data-testid="customer-licenses-tab-content">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Extension Licenses</h3>
              <p className="text-sm text-gray-500">Manage Joomla extension licenses for this customer</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchLicenses} disabled={licenseLoading}>
                <RefreshCw className={`w-4 h-4 mr-1 ${licenseLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => { fetchExtensionProducts(); setGrantModalOpen(true); }}
                data-testid="grant-license-button"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Product
              </Button>
            </div>
          </div>

          {licenseLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
            </div>
          ) : licenses.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Key className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">No licenses assigned yet</p>
                <Button onClick={() => { fetchExtensionProducts(); setGrantModalOpen(true); }} data-testid="grant-first-license-button">
                  <Plus className="w-4 h-4 mr-1" /> Add First Product
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {licenses.map((lic) => {
                const isActive = lic.status === 'active';
                const isExpired = lic.expires_at && new Date(lic.expires_at) < new Date();
                const tierColors = {
                  free: 'bg-gray-100 text-gray-700',
                  single: 'bg-blue-100 text-blue-700',
                  multi: 'bg-purple-100 text-purple-700',
                  developer: 'bg-indigo-100 text-indigo-700',
                  lifetime: 'bg-amber-100 text-amber-700',
                };
                const tierLabels = {
                  free: 'Free', single: 'Single Site', multi: 'Multi-Site',
                  developer: 'Developer', lifetime: 'Lifetime',
                };
                return (
                  <Card key={lic.id} className={`border ${!isActive ? 'opacity-60 border-red-200 bg-red-50' : isExpired ? 'border-orange-200 bg-orange-50' : 'border-gray-200'}`} data-testid={`license-row-${lic.id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {/* Product + Tier */}
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <Shield className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            <span className="font-semibold text-gray-900 truncate">{lic.primary_product_name || lic.elements?.join(', ') || 'Unknown Product'}</span>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${tierColors[lic.license_type] || 'bg-gray-100 text-gray-700'}`}>
                              {tierLabels[lic.license_type] || lic.license_type}
                            </span>
                            {!isActive && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">REVOKED</span>}
                            {isActive && isExpired && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">EXPIRED</span>}
                            {lic.admin_notes && <span className="text-xs text-gray-500 italic truncate max-w-xs">{lic.admin_notes}</span>}
                          </div>

                          {/* Key */}
                          <div className="flex items-center gap-2 mb-2">
                            <code className="text-xs font-mono bg-gray-100 px-2 py-1 rounded text-gray-800 border select-all">{lic.key}</code>
                            <button onClick={() => copyKey(lic.key)} className="text-gray-400 hover:text-blue-600 transition-colors" title="Copy key" data-testid={`copy-key-${lic.id}`}>
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          </div>

                          {/* Meta row */}
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Globe className="w-3 h-3" />
                              Sites: {lic.sites?.length || 0} / {lic.max_sites === null ? '∞' : lic.max_sites}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {lic.expires_at
                                ? `Expires ${new Date(lic.expires_at).toLocaleDateString()}`
                                : 'Never expires'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Granted {new Date(lic.created_at).toLocaleDateString()}
                            </span>
                            {lic.order_id === 'admin-grant' && (
                              <span className="text-blue-600 font-medium">Admin Grant</span>
                            )}
                          </div>

                          {/* Joomla 911 master toggle */}
                          <div className="flex items-center gap-2 mt-2" data-testid={`license-joomla911-toggle-row-${lic.id}`}>
                            <Switch
                              checked={lic.joomla911_enabled !== false}
                              onCheckedChange={(checked) => handleToggleLicenseJoomla911(lic.id, checked)}
                              data-testid={`license-joomla911-switch-${lic.id}`}
                            />
                            <span className="text-xs text-gray-600">
                              Joomla 911 (AI Malware Scanner): {lic.joomla911_enabled !== false
                                ? <span className="text-green-600 font-medium">Enabled for this license</span>
                                : <span className="text-red-600 font-medium">Revoked for this license</span>}
                            </span>
                          </div>

                          {/* Registered sites */}
                          {lic.sites?.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {lic.sites.map((s, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded border border-blue-200 w-fit" data-testid={`license-site-row-${lic.id}-${s.key}`}>
                                  <span>{s.url}</span>
                                  <span className="text-blue-300">|</span>
                                  <Switch
                                    checked={s.joomla911_enabled !== false}
                                    onCheckedChange={(checked) => handleToggleSiteJoomla911(lic.id, s.key, checked)}
                                    data-testid={`license-site-joomla911-switch-${lic.id}-${s.key}`}
                                  />
                                  <span className={s.joomla911_enabled !== false ? 'text-green-700' : 'text-red-600 font-medium'}>
                                    {s.joomla911_enabled !== false ? 'Joomla 911 on' : 'Joomla 911 revoked'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditLicenseModal({ ...lic, _expires_at_input: lic.expires_at ? lic.expires_at.split('T')[0] : '', _max_sites_input: lic.max_sites !== null ? String(lic.max_sites) : '' })}
                            data-testid={`edit-license-${lic.id}`}
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          {isActive ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleRevokeLicense(lic.id)}
                              data-testid={`revoke-license-${lic.id}`}
                            >
                              <Ban className="w-3.5 h-3.5 mr-1" /> Revoke
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => handleReactivateLicense(lic.id)}
                              data-testid={`reactivate-license-${lic.id}`}
                            >
                              <CheckCircle className="w-3.5 h-3.5 mr-1" /> Reactivate
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ── Grant License Modal ────────────────────────────────────────── */}
      <Dialog open={grantModalOpen} onOpenChange={setGrantModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Award className="w-5 h-5 text-blue-600" /> Grant License to {customer?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleGrantLicense} className="space-y-5 mt-2">
            {/* Product Picker */}
            <div>
              <Label className="text-sm font-medium">Product <span className="text-red-500">*</span></Label>
              <select
                value={grantForm.product_id}
                onChange={e => {
                  const pid = e.target.value;
                  const prod = extensionProducts.find(p => p.id === pid);
                  setGrantForm(f => ({ ...f, product_id: pid, license_tier: prod?.has_license_options ? 'single' : 'single' }));
                }}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                data-testid="grant-product-select"
              >
                <option value="">— Select a Joomla extension —</option>
                {extensionProducts.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {extensionProducts.length === 0 && (
                <p className="text-xs text-gray-400 mt-1">No extension products found. Create products in the catalog first.</p>
              )}
            </div>

            {/* License Tier */}
            <div>
              <Label className="text-sm font-medium">License Tier</Label>
              {/* Show tier cards if product has license_options */}
              {(() => {
                const prod = extensionProducts.find(p => p.id === grantForm.product_id);
                const opts = prod?.license_options?.filter(o => o.visible !== false) || [];
                if (opts.length > 0) {
                  return (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {opts.map(opt => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setGrantForm(f => ({ ...f, license_tier: opt.license_type || opt.id, price: opt.price != null ? String(opt.price) : f.price }))}
                          className={`text-left p-3 rounded-xl border-2 transition-all ${grantForm.license_tier === (opt.license_type || opt.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                          data-testid={`tier-card-${opt.id}`}
                        >
                          <div className="font-semibold text-sm text-gray-900">
                            {opt.name || opt.label || (opt.license_type ? opt.license_type.charAt(0).toUpperCase() + opt.license_type.slice(1) : opt.id)}
                          </div>
                          {opt.price != null && <div className="text-xs text-blue-600 font-bold">${opt.price}</div>}
                          {opt.subtitle && <div className="text-xs text-gray-500 mt-0.5">{opt.subtitle}</div>}
                        </button>
                      ))}
                    </div>
                  );
                }
                return (
                  <select
                    value={grantForm.license_tier}
                    onChange={e => setGrantForm(f => ({ ...f, license_tier: e.target.value }))}
                    className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="grant-tier-select"
                  >
                    <option value="free">Free</option>
                    <option value="single">Single Site (1 site)</option>
                    <option value="multi">Multi-Site (unlimited)</option>
                    <option value="developer">Developer (unlimited + priority support)</option>
                    <option value="lifetime">Lifetime (never expires)</option>
                  </select>
                );
              })()}
            </div>

            {/* Overrides */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Price Override <span className="text-gray-400">(record only)</span></Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={grantForm.price}
                    onChange={e => setGrantForm(f => ({ ...f, price: e.target.value }))}
                    placeholder="0.00"
                    className="w-full border rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="grant-price-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Max Sites <span className="text-gray-400">(blank = tier default)</span></Label>
                <input
                  type="number"
                  min="0"
                  value={grantForm.max_sites}
                  onChange={e => setGrantForm(f => ({ ...f, max_sites: e.target.value }))}
                  placeholder="Blank = unlimited for VIP/dev"
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="grant-max-sites-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Expiry Date <span className="text-gray-400">(blank = tier default)</span></Label>
                <input
                  type="date"
                  value={grantForm.expires_at}
                  onChange={e => setGrantForm(f => ({ ...f, expires_at: e.target.value }))}
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="grant-expiry-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Quantity</Label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={grantForm.quantity}
                  onChange={e => setGrantForm(f => ({ ...f, quantity: e.target.value }))}
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="grant-quantity-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Internal Notes <span className="text-gray-400">(admin only)</span></Label>
              <textarea
                rows={2}
                value={grantForm.notes}
                onChange={e => setGrantForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="e.g. Complimentary license for partnership deal"
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                data-testid="grant-notes-input"
              />
            </div>

            <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-100 rounded-xl">
              <input
                id="send-email-cb"
                type="checkbox"
                checked={grantForm.send_email}
                onChange={e => setGrantForm(f => ({ ...f, send_email: e.target.checked }))}
                className="w-4 h-4 accent-blue-600"
                data-testid="grant-send-email-checkbox"
              />
              <label htmlFor="send-email-cb" className="text-sm text-gray-700 cursor-pointer flex items-center gap-1">
                <Send className="w-3.5 h-3.5 text-blue-600" />
                Notify customer by email with their license key(s)
              </label>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setGrantModalOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={grantLoading || !grantForm.product_id} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="grant-license-submit">
                {grantLoading ? 'Granting...' : `Grant ${grantForm.quantity > 1 ? grantForm.quantity + ' Licenses' : 'License'}`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Edit License Modal ─────────────────────────────────────────── */}
      {editLicenseModal && (
        <Dialog open={!!editLicenseModal} onOpenChange={() => setEditLicenseModal(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit2 className="w-4 h-4" /> Edit License
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-semibold text-sm text-gray-800">{editLicenseModal.primary_product_name}</p>
                <code className="text-xs font-mono text-gray-500">{editLicenseModal.key}</code>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">License Tier</Label>
                  <select
                    value={editLicenseModal.license_type}
                    onChange={e => setEditLicenseModal(m => ({ ...m, license_type: e.target.value }))}
                    className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="edit-license-tier-select"
                  >
                    <option value="free">Free</option>
                    <option value="single">Single Site</option>
                    <option value="multi">Multi-Site</option>
                    <option value="developer">Developer</option>
                    <option value="lifetime">Lifetime</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Status</Label>
                  <select
                    value={editLicenseModal.status}
                    onChange={e => setEditLicenseModal(m => ({ ...m, status: e.target.value }))}
                    className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="edit-license-status-select"
                  >
                    <option value="active">Active</option>
                    <option value="canceled">Canceled</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Max Sites <span className="text-gray-400">(0 = unlimited)</span></Label>
                  <input
                    type="number"
                    min="0"
                    value={editLicenseModal._max_sites_input}
                    onChange={e => setEditLicenseModal(m => ({ ...m, _max_sites_input: e.target.value }))}
                    className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="edit-max-sites-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Expiry Date <span className="text-gray-400">(blank = never)</span></Label>
                  <input
                    type="date"
                    value={editLicenseModal._expires_at_input}
                    onChange={e => setEditLicenseModal(m => ({ ...m, _expires_at_input: e.target.value }))}
                    className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="edit-expiry-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">Admin Notes</Label>
                <textarea
                  rows={2}
                  value={editLicenseModal.admin_notes || ''}
                  onChange={e => setEditLicenseModal(m => ({ ...m, admin_notes: e.target.value }))}
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="edit-notes-input"
                />
              </div>
            </div>

            <DialogFooter className="gap-2 mt-4">
              <Button variant="outline" onClick={() => setEditLicenseModal(null)}>Cancel</Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => handleUpdateLicense(editLicenseModal.id, {
                  license_tier: editLicenseModal.license_type,
                  status: editLicenseModal.status,
                  max_sites: editLicenseModal._max_sites_input !== '' ? parseInt(editLicenseModal._max_sites_input, 10) : null,
                  expires_at: editLicenseModal._expires_at_input || 'none',
                  notes: editLicenseModal.admin_notes || null,
                })}
                data-testid="edit-license-save"
              >
                <Save className="w-4 h-4 mr-1" /> Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Address Modal */}
      <Dialog open={addressModalOpen} onOpenChange={setAddressModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAddress ? 'Edit Address' : 'Add New Address'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Address Label</Label>
              <Select value={addressForm.label} onValueChange={(v) => setAddressForm({...addressForm, label: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Home">Home</SelectItem>
                  <SelectItem value="Work">Work</SelectItem>
                  <SelectItem value="Office">Office</SelectItem>
                  <SelectItem value="Warehouse">Warehouse</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input
                  value={addressForm.first_name}
                  onChange={(e) => setAddressForm({...addressForm, first_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input
                  value={addressForm.last_name}
                  onChange={(e) => setAddressForm({...addressForm, last_name: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Street Address *</Label>
              <Input
                value={addressForm.address1}
                onChange={(e) => setAddressForm({...addressForm, address1: e.target.value})}
                placeholder="123 Main Street"
              />
            </div>
            <div>
              <Label>Apt, Suite, Unit (optional)</Label>
              <Input
                value={addressForm.address2}
                onChange={(e) => setAddressForm({...addressForm, address2: e.target.value})}
                placeholder="Apt 4B"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>City *</Label>
                <Input
                  value={addressForm.city}
                  onChange={(e) => setAddressForm({...addressForm, city: e.target.value})}
                />
              </div>
              <div>
                <Label>State *</Label>
                <Select value={addressForm.state} onValueChange={(v) => setAddressForm({...addressForm, state: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>ZIP Code *</Label>
                <Input
                  value={addressForm.zip_code}
                  onChange={(e) => setAddressForm({...addressForm, zip_code: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Phone (optional)</Label>
              <Input
                value={addressForm.phone}
                onChange={(e) => setAddressForm({...addressForm, phone: e.target.value})}
                placeholder="(555) 555-5555"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_default"
                checked={addressForm.is_default}
                onChange={(e) => setAddressForm({...addressForm, is_default: e.target.checked})}
                className="rounded border-gray-300"
              />
              <Label htmlFor="is_default" className="cursor-pointer">Set as default shipping address</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddressModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveAddress} disabled={savingAddress}>
              {savingAddress ? 'Saving...' : 'Save Address'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Detail Modal */}
      <Dialog open={orderDetailOpen} onOpenChange={setOrderDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Order {selectedOrder?.order_number}</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Badge className={getStatusConfig(selectedOrder.status).color}>
                  {selectedOrder.status}
                </Badge>
                <span className="text-sm text-gray-500">
                  {new Date(selectedOrder.created_at).toLocaleString()}
                </span>
              </div>

              {/* Items */}
              <div className="border rounded-lg divide-y">
                {selectedOrder.items?.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3">
                    <div>
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                    </div>
                    <span className="font-semibold">${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>${selectedOrder.subtotal?.toFixed(2)}</span>
                </div>
                {selectedOrder.shipping_cost > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>Shipping</span>
                    <span>${selectedOrder.shipping_cost?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span>Tax</span>
                  <span>${selectedOrder.tax?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                  <span>Total</span>
                  <span>${selectedOrder.total?.toFixed(2)}</span>
                </div>
              </div>

              {/* Shipping Address */}
              {selectedOrder.shipping_address && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Truck className="w-4 h-4" /> Shipping Address
                  </h4>
                  <p className="text-sm text-gray-600">
                    {selectedOrder.shipping_address.firstName} {selectedOrder.shipping_address.lastName}<br />
                    {selectedOrder.shipping_address.address1}<br />
                    {selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} {selectedOrder.shipping_address.zipCode}
                  </p>
                  {selectedOrder.tracking_number && (
                    <p className="text-sm mt-2">
                      <strong>Tracking:</strong> {selectedOrder.tracking_number}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Review Modal */}
      <Dialog open={reviewModalOpen} onOpenChange={setReviewModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Leave a Review
            </DialogTitle>
          </DialogHeader>
          {reviewingOrder && (
            <div className="space-y-4 py-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">Order: <span className="font-medium">{reviewingOrder.order_number}</span></p>
                <p className="text-xs text-gray-500">{reviewingOrder.items_count} items • {new Date(reviewingOrder.created_at).toLocaleDateString()}</p>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Your Rating</Label>
                <div className="mt-2">
                  {renderStars(reviewForm.rating, true, (rating) => setReviewForm({...reviewForm, rating}))}
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Review Title</Label>
                <Input
                  placeholder="Sum up your experience..."
                  value={reviewForm.title}
                  onChange={(e) => setReviewForm({...reviewForm, title: e.target.value})}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium">Your Review</Label>
                <Textarea
                  placeholder="Share your experience with these products..."
                  value={reviewForm.content}
                  onChange={(e) => setReviewForm({...reviewForm, content: e.target.value})}
                  rows={4}
                  className="mt-1"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmitReview} disabled={submittingReview}>
              {submittingReview ? 'Submitting...' : 'Submit Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCustomerDashboard;
